import {
  type SharedValue,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import {
  markOnboardingDestinationRevealed,
  useOnboardingState,
} from "@-/dev.improve.haniverse.lib.onboarding";
import {
  hasOnboardingReactionPlayed,
  type OnboardingState,
} from "@-/dev.improve.haniverse.lib.onboarding.state";
import { useEffect, useState } from "react";

/**
 * How long a newly unlocked destination waits before popping in: the onboarding sheet or the
 * globe it replaces moves first, so the eye is on the strip by then.
 */
const REVEAL_DELAY_MS = 450;
/** A pop with a little overshoot, like the icon landing in its slot. */
const REVEAL_SPRING = { damping: 9, mass: 0.6, stiffness: 180 } as const;

/** The destinations the onboarding hands out one at a time (see `lib/onboarding`). */
const ONBOARDING_REVEALED_DESTINATIONS = new Set([
  "browse",
  "decks",
  "learn",
  "map",
  "search",
  "stats",
]);

function shouldRevealDestination(state: OnboardingState | null, destination: string): boolean {
  return (
    state !== null &&
    // The globe covers the whole app until it becomes the map: the map pops in after it.
    hasOnboardingReactionPlayed(state, "intro") &&
    ONBOARDING_REVEALED_DESTINATIONS.has(destination) &&
    !state.revealedDestinations.includes(destination)
  );
}

/**
 * 0 → 1 as a destination the onboarding has just unlocked pops into the strip, once (it is then
 * recorded as revealed); 1 for every other destination. A destination that changes into another
 * (the map's tab turning into Learn's) pops again as the new one.
 */
export function useOnboardingDestinationReveal(destination: string): SharedValue<number> {
  const state = useOnboardingState();
  const reduceMotion = useReducedMotion();
  const shouldReveal = shouldRevealDestination(state, destination);
  const [isHiddenAtMount] = useState(shouldReveal && !reduceMotion);
  const progress = useSharedValue(isHiddenAtMount ? 0 : 1);
  useEffect(() => {
    if (!shouldReveal) {
      return;
    }
    if (!reduceMotion) {
      progress.set(0);
      progress.set(withDelay(REVEAL_DELAY_MS, withSpring(1, REVEAL_SPRING)));
    }
    markOnboardingDestinationRevealed(destination);
  }, [destination, progress, reduceMotion, shouldReveal]);
  return progress;
}
